/**
 * The nearest police station, asked of OpenStreetMap: where an arrested
 * player is taken (see `loaders/policeStations.ts`). The tiles loaded about
 * the player often hold none, since stations are few. Asked of Nominatim's
 * search first, which answers in a second or two, then of Overpass. Pure:
 * the questions, and the answers read.
 */

export interface StationPlace {
  lat: number;
  lon: number;
  name: string;
}

/** The Overpass query for police stations within `radius` metres of (lat, lon): points, and the middles of outlines, as JSON. */
export function policeQuery(lat: number, lon: number, radius: number): string {
  return `[out:json][timeout:20];nwr["amenity"="police"](around:${Math.round(radius)},${lat.toFixed(5)},${lon.toFixed(5)});out center tags 40;`;
}

export function policeUrl(endpoint: string, lat: number, lon: number, radius: number): string {
  return `${endpoint}?data=${encodeURIComponent(policeQuery(lat, lon, radius))}`;
}

interface Element {
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
}

/** The station nearest (lat, lon) in Overpass's JSON answer, by its point or its outline's middle; undefined with none, or an answer not understood. */
export function nearestStation(json: unknown, lat: number, lon: number): StationPlace | undefined {
  const elements = (json as { elements?: Element[] } | undefined)?.elements;
  if (!Array.isArray(elements)) return undefined;
  const cos = Math.cos((lat * Math.PI) / 180);
  let best: StationPlace | undefined, bestD = Infinity;
  for (const e of elements) {
    const at = e.center ?? (e.lat !== undefined && e.lon !== undefined ? { lat: e.lat, lon: e.lon } : undefined);
    if (at === undefined) continue;
    const d = Math.hypot(at.lat - lat, (at.lon - lon) * cos);
    if (d >= bestD) continue;
    bestD = d;
    best = { lat: at.lat, lon: at.lon, name: e.tags?.name ?? '' };
  }
  return best;
}

/** Nominatim's search for police stations in a box `radius` metres about (lat, lon), and no further. */
export function nominatimUrl(lat: number, lon: number, radius: number): string {
  const dLat = radius / 111_320, dLon = dLat / Math.max(0.1, Math.cos((lat * Math.PI) / 180));
  const box = [lon - dLon, lat + dLat, lon + dLon, lat - dLat].map(v => v.toFixed(4)).join(',');
  return `https://nominatim.openstreetmap.org/search?q=police&format=jsonv2&limit=20&bounded=1&viewbox=${box}`;
}

/** The police station nearest (lat, lon) in Nominatim's answer: its places tagged as police, nothing else it matched. */
export function nearestFound(json: unknown, lat: number, lon: number): StationPlace | undefined {
  if (!Array.isArray(json)) return undefined;
  const elements: Element[] = [];
  for (const e of json as Array<{ category?: string; type?: string; lat?: string; lon?: string; name?: string }>) {
    if (e.category !== 'amenity' || e.type !== 'police' || e.lat === undefined || e.lon === undefined) continue;
    elements.push({ lat: Number(e.lat), lon: Number(e.lon), tags: { name: e.name ?? '' } });
  }
  return nearestStation({ elements }, lat, lon);
}
